import useSWR from 'swr'
import { DatumTags, DatumType } from '@crystal-eyes/types'
import { gql, queryWithAuth } from '@crystal-eyes/utils/apis/graphqlApi'
import useAuth from '@crystal-eyes/hooks/useAuth'

interface Identity {
  id: string
}

type Options = {
  identity?: Identity
  withIdentity?: Identity
  tags: DatumTags[]
  type: DatumType
  limit: number
}

export type State = {
  data?: { id: string; text: string }[]
  loading?: boolean
  error?: any
}

const DATUM_QUERY = gql`
  query UseRelationshipDatums(
    $id: Guid!
    $with: Guid!
    $tags: [PersonalityDatumTag]!
    $limit: Int!
  ) {
    identity(id: $id) {
      snapshot {
        personality {
          id

          relationshipPhrases(withId: $with, tags: $tags, limit: $limit) {
            id
            text
          }
        }
      }
    }
  }
`

export default function useRelationshipDatums({
  identity,
  withIdentity,
  tags,
  type,
  limit,
}: Options): State {
  const { data: auth } = useAuth()

  const fetcher = ([_key, _authedUser, variables]: [string, string, object]) =>
    queryWithAuth(auth, DATUM_QUERY, variables)

  const query =
    identity && withIdentity
      ? [
          'useRelationshipDatums',
          auth?.jwtPayload?.sub,
          {
            id: identity.id,
            with: withIdentity.id,
            tags: [type, ...tags],
            limit,
          },
        ]
      : null

  const {
    data: queryData,
    isLoading: loading,
    error: error,
  } = useSWR(query, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
    fallbackData: { identity },
  })

  return {
    data: queryData?.identity?.snapshot?.personality?.relationshipPhrases,
    loading,
    error,
  }
}
